/**
 * セクション間の隙間にホバーするとプラスボタンが現れる UI。
 * 仕様書: docs/01_spec/spec_mail_system.md B-16 (Phase 22c)
 *
 * - 0.5 秒ホバーで隙間が広がりプラスボタンがフェードイン
 * - クリックで AddSectionButton と同じドロップダウンを表示
 * - クリック → 該当位置にセクションを挿入
 */
import { Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { MailSectionType } from '@goenhub/shared';

interface Props {
  /** クリック時に挿入するセクション型を受け取って実際の挿入を行う */
  onInsert: (type: MailSectionType) => void;
}

const SECTION_OPTIONS: Array<{ type: MailSectionType; label: string }> = [
  { type: 'greeting', label: '冒頭あいさつ' },
  { type: 'lead', label: 'リード文' },
  { type: 'body', label: '本文' },
  { type: 'cta', label: 'CTA' },
  { type: 'closing', label: '締め' },
  { type: 'ps', label: '追伸' },
];

const HOVER_DELAY_MS = 500;

export function SectionGapInserter({ onInsert }: Props) {
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const hoverTimerRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 外側クリックで閉じる
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const onEnter = () => {
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = window.setTimeout(() => setHovered(true), HOVER_DELAY_MS);
  };
  const onLeave = () => {
    if (hoverTimerRef.current) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (!menuOpen) setHovered(false);
  };

  const expanded = hovered || menuOpen;

  return (
    <div
      ref={containerRef}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className="relative transition-all duration-200 ease-out"
      style={{ height: expanded ? 36 : 8, marginTop: 0, marginBottom: 0 }}
    >
      {/* 細い見えないクリック領域（ホバー判定用） */}
      <div className="absolute inset-x-0 inset-y-0" />

      {expanded && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen(!menuOpen);
          }}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-7 rounded-full bg-[var(--accent)] text-white flex items-center justify-center shadow-md hover:scale-110 transition-transform"
          title="ここにセクションを挿入"
          aria-label="セクションを挿入"
        >
          <Plus className="size-4" />
        </button>
      )}

      {menuOpen && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-30 rounded border border-[var(--border)] bg-[var(--bg-surface)] shadow-lg overflow-hidden min-w-[180px]"
        >
          {SECTION_OPTIONS.map((opt) => (
            <button
              key={opt.type}
              type="button"
              onClick={() => {
                onInsert(opt.type);
                setMenuOpen(false);
                setHovered(false);
              }}
              className="w-full flex items-baseline gap-2 px-3 py-1.5 text-left hover:bg-[var(--bg-hover)] transition-colors"
            >
              <span className="text-[12px] font-medium text-[var(--text-primary)]">
                {opt.label}
              </span>
              <code className="ml-auto text-[10px] font-mono text-[var(--text-muted)]">
                {opt.type}
              </code>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
